import React, { useState, useEffect } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell, Heart, MessageCircle, Bookmark } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  type Notification,
} from '../../api/notifications.api';
import { useAuthStore } from '../../store/authStore';
import { NotificationItemSkeleton } from './NotificationItemSkeleton';

export const NotificationBell: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const loadCount = () => getUnreadCount().then(setUnread).catch(() => {});
    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!open || !isAuthenticated) return;
    setNotifLoading(true);
    getNotifications()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setNotifLoading(false));
  }, [open, isAuthenticated]);

  if (!isAuthenticated) return null;

  const handleItemClick = async (n: Notification) => {
    if (!n.read) {
      await markAsRead(n._id);
      setUnread(Math.max(0, unread - 1));
      setItems(items.map((x) => (x._id === n._id ? { ...x, read: true } : x)));
    }
    navigate(`/gallery/${n.albumId}`);
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    setUnread(0);
    setItems(items.map((x) => ({ ...x, read: true })));
  };

  const formatMessage = (n: Notification) => {
    const actor = n.actorName || '누군가';
    const title = n.albumTitle || '내 앨범';
    if (n.type === 'like') return `${actor}님이 "${title}"에 좋아요를 눌렀어요`;
    if (n.type === 'comment') return `${actor}님이 "${title}"에 댓글을 남겼어요`;
    if (n.type === 'bookmark') return `${actor}님이 "${title}"를 북마크했어요`;
    return '';
  };

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          className="relative p-2 rounded-lg text-white hover:bg-white/20 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="알림"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-[60] w-80 max-w-[90vw] bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">알림</h3>
            {items.some((i) => !i.read) && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-600 hover:underline"
              >
                모두 읽음
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifLoading ? (
              <>
                <NotificationItemSkeleton />
                <NotificationItemSkeleton />
                <NotificationItemSkeleton />
              </>
            ) : items.length === 0 ? (
              <div className="p-6 text-sm text-gray-500 dark:text-slate-400 text-center">
                알림이 없습니다
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n._id}
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-start gap-2 ${
                    !n.read ? 'bg-primary-50/50' : ''
                  }`}
                >
                  <div className="shrink-0 mt-0.5">
                    {n.type === 'like' && (
                      <Heart className="h-4 w-4 text-rose-500" />
                    )}
                    {n.type === 'comment' && (
                      <MessageCircle className="h-4 w-4 text-primary-500" />
                    )}
                    {n.type === 'bookmark' && (
                      <Bookmark className="h-4 w-4 text-amber-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 dark:text-slate-100 line-clamp-2">
                      {formatMessage(n)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                      {new Date(n.createdAt).toLocaleDateString('ko-KR')}
                    </p>
                  </div>
                  {!n.read && (
                    <div className="w-2 h-2 bg-primary-500 rounded-full shrink-0 mt-1" />
                  )}
                </button>
              ))
            )}
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
